import { _ } from "../../../core/polyglot/polyglot.js";
import { infoCircleIcon } from "../../../core/elements/icons/index.js";
import { widget } from "@foretagsplatsen/widgetjs";
import keyFigureValueFormatter from "../../../models/keyFigure/rendering/keyFigureValueFormatter.js";
import popoverWidget from "../../../core/widgets/popoverWidget/popoverWidget.js";

/**
 *
 * `keyFigureValueWidget` is a simple widget used to render some interesting
 * values about a single keyfigure (current value, min, max, average?).
 *
 * @param {object} spec - Parameter object
 * @param {number} [spec.current] - current value of the key-figure
 * @param {Date} [spec.currentDate] - date for the current value of the key-figure
 * @param {number} [spec.min] - minimum value of the key-figure
 * @param {number} [spec.max] - maximum value of the key-figure
 * @param {Date} [spec.minDate] - date when the minimum value was reached
 * @param {Date} [spec.maxDate] - date when the maximum value was reached
 * @param {boolean} [navigateToSingleKeyFigure] - flag to add a button redirecting to single keyFigure
 * @param {object} my - protected fields holder
 * @return {*}
 */
function keyFigureValueWidget(spec, my) {
	spec ||= {};
	my ||= {};

	const that = widget(spec, my);

	my.min = spec.min;
	my.max = spec.max;
	my.minDate = spec.minDate;
	my.maxDate = spec.maxDate;
	my.current = spec.current;
	my.currentDate = spec.currentDate;

	my.name = "";
	my.description = "";

	my.navigateToSingleKeyFigure = spec.navigateToSingleKeyFigure;

	let lastCurrentData;

	my.popover = popoverWidget({
		popoverCssClass: "popover popover-condensed fade top",
		header: function (html) {
			html.span(html.strong(my.name));
		},
		content: function (html) {
			html.render(my.description);
		},
		primaryLabel: my.navigateToSingleKeyFigure ? _("View KPI") : null,
		primaryAction: function () {
			my.redirectTo("single-kpi", { keyFigure: my.type });
		},
	});

	that.setResult = function (keyFigureResult) {
		const keyFigure = keyFigureResult.getFirstKeyFigure();
		const formatter = keyFigureValueFormatter.formatterFor(keyFigure);
		const values = keyFigureResult.getValues();
		my.name = keyFigure.getName();
		my.description = keyFigure.getDescription();
		my.type = keyFigure.getId();

		my.min = formatter(keyFigureResult.getMin().getValue());
		my.max = formatter(keyFigureResult.getMax().getValue());

		my.minPeriod = keyFigureResult.getMin().getPeriod().toSmartString();
		my.maxPeriod = keyFigureResult.getMax().getPeriod().toSmartString();

		lastCurrentData = values.at(-1);

		that.setCurrentValue();
	};

	that.setCurrentValue = function (value) {
		value ||= lastCurrentData;
		const formatter = keyFigureValueFormatter.formatterFor(value);

		my.current = value.getValue() ? formatter(value.getValue()) : undefined;
		my.currentDate = value.getPeriod().toSmartString();

		that.update();
	};

	that.renderContentOn = function (html) {
		html.div(
			{ class: "panel kpi-report-panel" },
			html.div(
				{ class: "panel-body no-target" },
				my.renderKeyFigureValueOn,
				html.p(
					{ class: "text-center kpi-value-date" },
					my.currentDate ? `(${my.currentDate})` : "",
				),
				my.renderTableOn,
			),
		);
	};

	my.renderKeyFigureValueOn = function (html) {
		const trigger = html.div(
			{ class: "amount" },
			(html) => {
				if (!my.description) return;

				html.a({
					"data-test": "kpi-info",
					class: [
						infoCircleIcon.getCssClass(),
						"pull-right",
						"kpi-value-info",
					],
				});
			},
			html.p({ class: "text-center kpi-value" }, my.current || "-"),
		);

		my.popover.setTriggerItem(trigger);
	};

	my.renderTableOn = function (html) {
		html.table(
			{
				class: "kpi-value-table table table-tight table-noborder table-striped table-striped-desaturated",
			},
			html.tbody(
				html.tr(
					html.td("Min"),
					html.td(
						`${my.min || "-"} `,
						my.minPeriod
							? html.span(
									{ class: "text-muted" },
									`(${my.minPeriod})`,
								)
							: "",
					),
				),
				html.tr(
					html.td("Max"),
					html.td(
						`${my.max || "-"} `,
						my.maxPeriod
							? html.span(
									{ class: "text-muted" },
									`(${my.maxPeriod})`,
								)
							: "",
					),
				),
			),
		);
	};

	return that;
}

export default keyFigureValueWidget;
